/**
 * Rent Schedule Service - Central Calculation Engine
 * All rent schedule calculations are handled here to avoid duplication.
 */

import { roundMoney, getRentAnnualisation } from '../utils/calculations';

const PAYMENT_PERIODS_PER_YEAR = Object.freeze({
  weekly: 52,
  fortnightly: 26,
  monthly: 12,
  quarterly: 4,
  annual: 1
});

const PAYMENT_DAYS = Object.freeze({
  weekly: 7,
  fortnightly: 14
});

const PAYMENT_MONTHS = Object.freeze({
  monthly: 1,
  quarterly: 3,
  annual: 12
});

const normaliseFrequency = (value, fallback = 'weekly') => String(value || fallback).trim().toLowerCase();

export const getPaymentFrequencyPeriodsPerYear = (paymentFrequency = 'weekly') => {
  return PAYMENT_PERIODS_PER_YEAR[normaliseFrequency(paymentFrequency)] || 0;
};

export const isSupportedPaymentFrequency = (paymentFrequency = 'weekly') => {
  return getPaymentFrequencyPeriodsPerYear(paymentFrequency) > 0;
};

const addCalendarMonthsClamped = (sourceDate, monthsToAdd) => {
  const source = new Date(sourceDate);
  const targetDay = source.getDate();
  const result = new Date(source);
  result.setDate(1);
  result.setMonth(result.getMonth() + monthsToAdd);
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(targetDay, lastDay));
  return result;
};

const addCalendarDays = (sourceDate, daysToAdd) => {
  const result = new Date(sourceDate);
  result.setDate(result.getDate() + daysToAdd);
  return result;
};

/**
 * Return the exact rent conversion used for one schedule period.
 * Example: $2,600 monthly rent = $31,200 annual rent.
 * If paid fortnightly: $31,200 / 26 = $1,200 gross per payment period.
 */
export const getRentPeriodCalculation = (rentAmount, rentBasis = 'weekly', paymentFrequency = 'weekly') => {
  const annualisation = getRentAnnualisation(rentAmount, rentBasis);
  const frequency = normaliseFrequency(paymentFrequency);
  const paymentPeriodsPerYear = getPaymentFrequencyPeriodsPerYear(frequency);
  const supported = annualisation.supported && paymentPeriodsPerYear > 0;
  const grossRentPerPeriod = supported ? roundMoney(annualisation.annualRent / paymentPeriodsPerYear) : 0;

  return {
    supported,
    rentAmount: annualisation.rentAmount,
    rentBasis: annualisation.rentBasis,
    rentBasisPeriodsPerYear: annualisation.periodsPerYear,
    annualRent: annualisation.annualRent,
    weeklyEquivalent: annualisation.weeklyEquivalent,
    monthlyEquivalent: annualisation.monthlyEquivalent,
    paymentFrequency: frequency,
    paymentPeriodsPerYear,
    grossRentPerPeriod
  };
};

/**
 * Calculate rent period dates using calendar-aware boundaries.
 * Weekly/fortnightly periods use exact calendar-day increments.
 * Monthly/quarterly/annual periods advance from the original lease start date,
 * clamping month-end dates where necessary so dates do not drift over time.
 * Custom frequency is intentionally not guessed and returns no periods.
 */
export const calculateRentPeriods = (startDate, endDate, paymentFrequency, rentBasis) => {
  const periods = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  const frequency = normaliseFrequency(paymentFrequency);

  if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end || !isSupportedPaymentFrequency(frequency)) {
    return periods;
  }

  const dayStep = PAYMENT_DAYS[frequency] || 0;
  const monthStep = PAYMENT_MONTHS[frequency] || 0;
  let periodNumber = 1;

  while (periodNumber <= 520) {
    const index = periodNumber - 1;
    const periodStart = dayStep
      ? addCalendarDays(start, dayStep * index)
      : addCalendarMonthsClamped(start, monthStep * index);

    if (periodStart > end) break;

    const nextPeriodStart = dayStep
      ? addCalendarDays(start, dayStep * (index + 1))
      : addCalendarMonthsClamped(start, monthStep * (index + 1));

    let scheduledPeriodEnd = new Date(nextPeriodStart);
    scheduledPeriodEnd.setDate(scheduledPeriodEnd.getDate() - 1);
    const isPartialPeriod = scheduledPeriodEnd > end;
    const periodEnd = isPartialPeriod ? new Date(end) : scheduledPeriodEnd;

    periods.push({
      periodNumber,
      periodStart: periodStart.getTime(),
      periodEnd: periodEnd.getTime(),
      dueDate: periodStart.getTime(),
      isPartialPeriod
    });

    periodNumber++;
  }

  return periods;
};

/**
 * Calculate gross rent for a payment period from the recorded rent amount/basis.
 */
export const calculateGrossRentForPeriod = (rentAmount, rentBasis, paymentFrequency) => {
  return getRentPeriodCalculation(rentAmount, rentBasis, paymentFrequency).grossRentPerPeriod;
};

/**
 * Calculate agent fee for a period.
 */
export const calculateAgentFee = (grossRent, agentFeePercent) => {
  const feePercent = parseFloat(agentFeePercent) || 0;
  return roundMoney(grossRent * (feePercent / 100));
};

/**
 * Calculate total deductions (agent fee + other fees).
 */
export const calculateTotalDeductions = (agentFee, otherFees = 0) => {
  return roundMoney((agentFee || 0) + (otherFees || 0));
};

/**
 * Calculate net owner amount.
 */
export const calculateNetOwnerAmount = (grossRent, totalDeductions) => {
  return roundMoney((grossRent || 0) - (totalDeductions || 0));
};

/**
 * Legacy amount-based status helper. The PocketBase paidToDate field is a date,
 * so generated schedule rows do not use this helper to infer status.
 */
export const determinePaymentStatus = (amountPaid, grossRent) => {
  const paid = parseFloat(amountPaid) || 0;
  const gross = parseFloat(grossRent) || 0;

  if (gross <= 0) return 'No Rent';
  if (paid <= 0) return 'Unpaid';
  if (paid >= gross) return 'Paid';
  if (paid > 0 && paid < gross) return 'Partial';

  return 'Unpaid';
};

/**
 * Calculate payment position (arrears, current, ahead).
 */
export const calculatePaymentPosition = (totalPaid, totalExpected) => {
  const paid = parseFloat(totalPaid) || 0;
  const expected = parseFloat(totalExpected) || 0;

  if (expected <= 0) return 'N/A';

  const difference = paid - expected;

  if (difference >= 0) {
    return `Ahead $${difference.toFixed(2)}`;
  }
  return `Arrears $${Math.abs(difference).toFixed(2)}`;
};

/**
 * Generate complete rent schedule rows.
 * Legacy frontend naming uses weeklyRent, but the amount is interpreted according
 * to rentBasis. The calculation snapshot is saved in row metadata for audit/help UI.
 */
export const generateRentSchedule = (leaseData, agentFeePercent = 0, otherDeductions = 0) => {
  const {
    startDate,
    endDate,
    weeklyRent,
    rentAmount,
    rentBasis = 'weekly',
    paymentFrequency = 'weekly',
    rentInAdvance = 0,
    existingSchedule = []
  } = leaseData;

  const sourceRentAmount = rentAmount ?? weeklyRent;
  if (!startDate || !endDate || sourceRentAmount === null || sourceRentAmount === undefined || sourceRentAmount === '') {
    return [];
  }

  const rentCalculation = getRentPeriodCalculation(sourceRentAmount, rentBasis, paymentFrequency);
  if (!rentCalculation.supported || rentCalculation.rentAmount <= 0) {
    return [];
  }

  const periods = calculateRentPeriods(startDate, endDate, paymentFrequency, rentBasis);
  if (periods.length === 0) return [];

  return periods.map((period, index) => {
    const grossRent = rentCalculation.grossRentPerPeriod;
    const agentFee = calculateAgentFee(grossRent, agentFeePercent);
    const totalDeductions = calculateTotalDeductions(agentFee, otherDeductions);
    const netOwnerAmount = calculateNetOwnerAmount(grossRent, totalDeductions);

    const existingRow = existingSchedule.find(r => r.periodNumber === (index + 1));
    const paidToDate = existingRow?.paidToDate || null;
    const status = existingRow?.status || 'Unpaid';

    return {
      periodNumber: index + 1,
      dueDate: period.dueDate,
      periodStart: period.periodStart,
      periodEnd: period.periodEnd,
      grossRentDue: grossRent,
      agentFeePercent,
      agentFeeAmount: agentFee,
      otherFees: otherDeductions,
      totalDeductions,
      netOwnerAmount,
      paidToDate,
      status,
      rentInAdvance,
      financialYear: getFinancialYearFromTimestamp(period.dueDate),
      metadata: {
        ...(existingRow?.metadata || {}),
        rentCalculation: {
          rentAmount: rentCalculation.rentAmount,
          rentBasis: rentCalculation.rentBasis,
          rentBasisPeriodsPerYear: rentCalculation.rentBasisPeriodsPerYear,
          annualRent: rentCalculation.annualRent,
          paymentFrequency: rentCalculation.paymentFrequency,
          paymentPeriodsPerYear: rentCalculation.paymentPeriodsPerYear,
          grossRentPerPeriod: grossRent,
          agentFeePercent: roundMoney(agentFeePercent),
          agentFeeAmount: agentFee,
          otherDeductions: roundMoney(otherDeductions),
          totalDeductions,
          netOwnerAmount,
          isPartialPeriod: period.isPartialPeriod
        }
      }
    };
  });
};

/**
 * Get Australian Financial Year from timestamp.
 */
export const getFinancialYearFromTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;

  if (month >= 7) {
    return `${year}-${String(year + 1).slice(-2)}`;
  }
  return `${year - 1}-${String(year).slice(-2)}`;
};

/**
 * Filter schedule rows for regeneration (only future unpaid rows).
 */
export const getRegenerableRows = (scheduleRows, rentIncreaseEffectiveDate) => {
  const effectiveDate = new Date(rentIncreaseEffectiveDate).getTime();

  const protectedStatuses = new Set(['paid', 'partial', 'waived', 'archived', 'disputed']);
  return scheduleRows.filter(row => {
    if (row.dueDate < effectiveDate) return false;
    if (protectedStatuses.has(String(row.status || '').trim().toLowerCase())) return false;
    return true;
  });
};

/**
 * Apply a rent increase to future unpaid rows using the lease's rent basis and
 * payment frequency. Older rows can fall back to their saved calculation metadata.
 */
export const applyRentIncrease = (
  scheduleRows,
  newRentAmount,
  effectiveDate,
  rentBasis = null,
  paymentFrequency = null
) => {
  const effectiveTimestamp = new Date(effectiveDate).getTime();

  const protectedStatuses = new Set(['paid', 'partial', 'waived', 'archived', 'disputed']);
  return scheduleRows.map(row => {
    if (protectedStatuses.has(String(row.status || '').trim().toLowerCase()) || row.dueDate < effectiveTimestamp) return row;

    const rowBasis = rentBasis || row.metadata?.rentCalculation?.rentBasis || 'weekly';
    const rowFrequency = paymentFrequency || row.metadata?.rentCalculation?.paymentFrequency || 'weekly';
    const rentCalculation = getRentPeriodCalculation(newRentAmount, rowBasis, rowFrequency);
    if (!rentCalculation.supported) return row;

    const grossRent = rentCalculation.grossRentPerPeriod;
    const explicitFeePercent = Number(row.agentFeePercent);
    const metadataFeePercent = Number(row.metadata?.rentCalculation?.agentFeePercent);
    const inferredFeePercent = Number(row.grossRentDue) > 0
      ? (Number(row.agentFeeAmount || 0) / Number(row.grossRentDue)) * 100
      : 0;
    const feePercent = Number.isFinite(explicitFeePercent)
      ? explicitFeePercent
      : Number.isFinite(metadataFeePercent)
        ? metadataFeePercent
        : inferredFeePercent;
    const agentFee = calculateAgentFee(grossRent, feePercent);
    const totalDeductions = calculateTotalDeductions(agentFee, row.otherFees);
    const netOwnerAmount = calculateNetOwnerAmount(grossRent, totalDeductions);

    return {
      ...row,
      grossRentDue: grossRent,
      agentFeeAmount: agentFee,
      totalDeductions,
      netOwnerAmount,
      metadata: {
        ...(row.metadata || {}),
        rentCalculation: {
          ...(row.metadata?.rentCalculation || {}),
          rentAmount: roundMoney(newRentAmount),
          rentBasis: rentCalculation.rentBasis,
          rentBasisPeriodsPerYear: rentCalculation.rentBasisPeriodsPerYear,
          annualRent: rentCalculation.annualRent,
          paymentFrequency: rentCalculation.paymentFrequency,
          paymentPeriodsPerYear: rentCalculation.paymentPeriodsPerYear,
          grossRentPerPeriod: grossRent,
          agentFeePercent: roundMoney(feePercent),
          agentFeeAmount: agentFee,
          otherDeductions: roundMoney(row.otherFees),
          totalDeductions,
          netOwnerAmount
        }
      }
    };
  });
};

/**
 * Calculate payment allocation across periods.
 * This legacy helper remains amount-based and will be reconciled with the
 * payments/payment_allocations collections in the dedicated payment stage.
 */
export const allocatePayment = (scheduleRows, paymentAmount, paymentDate) => {
  let remainingAmount = parseFloat(paymentAmount) || 0;
  const allocations = [];
  let totalAllocated = 0;

  const sortedRows = [...scheduleRows].sort((a, b) => a.dueDate - b.dueDate);

  for (const row of sortedRows) {
    if (remainingAmount <= 0) break;

    const legacyPaidAmount = typeof row.paidAmount === 'number' ? row.paidAmount : 0;
    const outstandingAmount = row.grossRentDue - legacyPaidAmount;
    const allocationAmount = Math.min(remainingAmount, outstandingAmount);

    if (allocationAmount > 0) {
      allocations.push({
        periodNumber: row.periodNumber,
        dueDate: row.dueDate,
        allocatedAmount: roundMoney(allocationAmount),
        previousPaid: legacyPaidAmount,
        newPaidTotal: roundMoney(legacyPaidAmount + allocationAmount)
      });

      remainingAmount -= allocationAmount;
      totalAllocated += allocationAmount;
    }
  }

  return {
    allocations,
    totalAllocated: roundMoney(totalAllocated),
    unallocatedAmount: roundMoney(remainingAmount)
  };
};

export const rentScheduleService = {
  getPaymentFrequencyPeriodsPerYear,
  isSupportedPaymentFrequency,
  getRentPeriodCalculation,
  calculateRentPeriods,
  calculateGrossRentForPeriod,
  calculateAgentFee,
  calculateTotalDeductions,
  calculateNetOwnerAmount,
  determinePaymentStatus,
  calculatePaymentPosition,
  generateRentSchedule,
  getFinancialYearFromTimestamp,
  getRegenerableRows,
  applyRentIncrease,
  allocatePayment
};

export default rentScheduleService;